import { dialog } from "../../../../../nq-component";
import BaseFormPresenter from "../../../../../base/BaseFormPresenter";
import DialogModal from "../../../../../components/Dialog";
import { findObjectUseCase } from "../../../../../usecases/object";
import { auditLogUseCase } from "../../../../../usecases/logs";
import {
  addDaysISO,
  getBusinessDate,
  isAfterFiveAMManila,
  logExtractedData,
  toISODate,
  toNumber,
} from "./utils/helperFunc";

class PartialLogsFormPresenter extends BaseFormPresenter {
  componentDidMount() {
    this.init();
    this.getObject();
    this.fetchInfiniteItems();
    this.getUserLoggedIn();
    this.loadCategories();
  }

  onChange(value, field) {
    this.change[field] = value;
    this.object[field] = value;

    // Preserve raw_materials if present in the view
    if (!this.object.raw_materials && this.view.state.object?.raw_materials) {
      this.object.raw_materials = [...this.view.state.object.raw_materials];
    }

    this.view.setObject(this.object);
  }

  async getUserLoggedIn() {
    const userLoggedIn = this.view.getCurrentUser();
    const query = {
      where: {
        id: userLoggedIn?.id,
      },
      include: ["storeAnalytic", "storeAnalytics"],
    };
    const user = await findObjectUseCase().execute("users", query);
    this.userLoggedIn = user;
    this.view.setUserLoggedIn(user);
  }

  async loadCategories() {
    try {
      const categories = await findObjectUseCase().execute("category", {
        limit: 1000,
        sort: { name: 1 },
      });

      this.view.setState({
        categories: categories.map((cat) => ({
          value: cat.id,
          label: cat.name,
        })),
      });
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }

  async getObject() {
    const collection = this.view.getCollectionName();
    const id = this.object.id || this.view.getObjectId();
    if (id) {
      const params = {
        include: ["raw_materials", "raw_materials.uom", "uom", "category"],
      };
      try {
        this.view.showProgress();
        this.object = await this.getObjectUseCase.execute(collection, id, {
          params,
        });
        this.view.hideProgress();

        this.view.setObject(this.object);
      } catch (error) {
        this.view.hideProgress();
        this.view.showError(error);
      }
    }
  }

  async fetchInfiniteItems(isInitial = false, keyword = "", categoryId = null) {
    this.view.setState({ isLoading: true });

    if (isInitial) {
      this.view.setState({
        skipRaw: 0,
        skipComp: 0,
        infiniteItems: [],
        hasMore: true,
      });
    }

    const skipRaw = isInitial ? 0 : (this.view.state.skipRaw ?? 0);
    const skipComp = isInitial ? 0 : (this.view.state.skipComp ?? 0);

    // shared where clause
    const whereClause = {};
    if (keyword) whereClause.itemName = { $regex: keyword, $options: "i" };
    if (categoryId) whereClause.category = { id: categoryId };

    const limit = 10;

    const [raws, comps] = await Promise.all([
      findObjectUseCase().execute("raw_materials", {
        where: whereClause,
        // limit,
        include: ["uom", "category"],
        skip: skipRaw,
      }),
      findObjectUseCase().execute("components", {
        where: whereClause,
        // limit,
        include: ["uom", "category"],
        keys: [
          "componentCost",
          "cost",
          "created",
          "id",
          "itemName",
          "rawCost",
          "raw_materials",
          "sku",
          "source",
          "uom",
          "updated",
        ],
        skip: skipComp,
      }),
    ]);

    const taggedRaws = (raws || []).map((x) => ({
      ...x,
      source: "raw_materials",
    }));
    const taggedComps = (comps || []).map((x) => ({
      ...x,
      source: "components",
    }));

    const merged = [...taggedRaws, ...taggedComps].sort((a, b) =>
      String(a.itemName || "").localeCompare(
        String(b.itemName || ""),
        undefined,
        { sensitivity: "base" },
      ),
    );

    this.view.setState((prev) => ({
      infiniteItems: isInitial ? merged : [...prev.infiniteItems, ...merged],
      skipRaw: skipRaw + (raws?.length || 0),
      skipComp: skipComp + (comps?.length || 0),
      hasMore: raws?.length === limit || comps?.length === limit,
      isLoading: false,
    }));
  }

  handleInfiniteScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    const { hasMore, isLoading } = this.view.state;

    if (
      scrollHeight - scrollTop <= clientHeight + 10 &&
      hasMore &&
      !isLoading
    ) {
      this.fetchInfiniteItems();
    }
  };

  createInventoryLogEntry(
    item,
    existingEditedRawMaterials,
    isUpdate,
    user,
    storeId,
  ) {
    const oldQuantity = isUpdate
      ? existingEditedRawMaterials[item.id]?.quantity ||
        item.stock_quantity ||
        "0"
      : item.stock_quantity || "0";
    const newQuantity = item.quantity || "0";

    return {
      user: { id: user.id },
      itemName: item.itemName,
      adjustmentType: "Partial Count",
      oldQuantity: String(oldQuantity),
      newQuantity: String(newQuantity),
      storeId: { id: storeId },
    };
  }

  async createInventoryLogs(data) {
    const { editedItems, existingEditedRawMaterials, isUpdate, user, storeId } =
      data;

    const editedItemsArray = Object.values(editedItems);

    const inventoryLogPromises = editedItemsArray.map(async (item) => {
      const logEntry = this.createInventoryLogEntry(
        item,
        existingEditedRawMaterials,
        isUpdate,
        user,
        storeId,
      );

      return await this.upsertUseCase.execute("inventory_logs", logEntry);
    });

    await Promise.all(inventoryLogPromises);
  }

  async createAuditLog(data) {
    const {
      user,
      auditMessage,
      collection,
      responseId,
      documentId,
      existingObject,
      response,
      payload,
      storeId,
      isUpdate,
    } = data;

    await auditLogUseCase().create({
      user: { id: user.id },
      logDate: new Date().toISOString(),
      message: auditMessage,
      collection,
      objectId: responseId || documentId,
      payload: {
        before: isUpdate ? existingObject : null,
        after: response || payload,
      },
      store: { id: storeId },
    });
  }

  generateAuditMessage(editedItems, existingEditedRawMaterials, isUpdate) {
    const editedItemsArray = Object.values(editedItems);

    if (isUpdate) {
      const changedItems = editedItemsArray
        .map((item) => {
          const oldQty =
            existingEditedRawMaterials[item.id]?.quantity ||
            item.stock_quantity ||
            "0";
          const newQty = item.quantity || "0";
          return `${item.itemName}: ${oldQty} → ${newQty}`;
        })
        .join(", ");

      return `Actual Count Updated: ${changedItems}`;
    }

    const itemCount = editedItemsArray.length;
    const itemNames = editedItemsArray
      .slice(0, 3)
      .map((i) => i.itemName)
      .join(", ");

    return `Actual Count Created: ${itemCount} item(s) adjusted${
      itemCount > 3 ? ` (${itemNames}...)` : ` (${itemNames})`
    }`;
  }

  mergeEditedRaw(existingEditedRaw, newEditedRawArray) {
    const mergedMap = new Map();

    // First, add all existing items
    existingEditedRaw.forEach((item) => {
      mergedMap.set(item.sku, item);
    });

    newEditedRawArray.forEach((item) => {
      mergedMap.set(item.sku, item);
    });

    return Array.from(mergedMap.values());
  }

  buildActualCountPayload(data) {
    const {
      actualCountDate,
      infiniteItems,
      existingEditedRawMaterials,
      editedItems,
      user,
      storeId,
      documentId,
      existingObject,
    } = data;

    const mergedEditedRawMaterials = {
      ...existingEditedRawMaterials,
      ...editedItems,
    };

    const existingEditedRaw = existingObject?.edited_raw || [];

    const newEditedRawArray = Object.values(editedItems).map((item) => ({
      sku: item.sku,
      quantity: item.quantity,
    }));

    const mergedEditedRaw = this.mergeEditedRaw(
      existingEditedRaw,
      newEditedRawArray,
    );
    const payload = {
      actualDate: actualCountDate,
      // raw_materials: infiniteItems,
      // edited_raw_materials: mergedEditedRawMaterials,
      edited_raw: mergedEditedRaw,
      adjustedBy: { id: user.id },
      storeId: { id: storeId },
      status: "Partial",
    };

    if (documentId) {
      payload.id = documentId;
    }

    return payload;
  }

  extractViewData() {
    const user = this.view.getCurrentUser();
    const storeId = user?.storeAnalytic?.[0]?.id;
    const actualCountDate = this.view.state.actualCountDate;
    const editedItems = this.view.state.selectedQuantities || {};
    const infiniteItems = this.view.state.infiniteItems || [];
    const existingObject = this.view.state.object || {};
    const existingEditedRawMaterials =
      existingObject.edited_raw_materials || {};
    const documentId = existingObject.id;

    return {
      user,
      storeId,
      actualCountDate,
      editedItems,
      infiniteItems,
      existingObject,
      existingEditedRawMaterials,
      documentId,
    };
  }

  async saveActual() {
    const collection = "actual_count_logs";

    const viewData = this.extractViewData();
    const {
      user,
      storeId,
      actualCountDate,
      editedItems,
      infiniteItems,
      existingObject,
      existingEditedRawMaterials,
      documentId,
    } = viewData;

    const isUpdate = !!documentId;

    logExtractedData(viewData, isUpdate);

    const payload = this.buildActualCountPayload({
      actualCountDate,
      infiniteItems,
      existingEditedRawMaterials,
      editedItems,
      user,
      storeId,
      documentId,
      existingObject,
    });

    this.view.showProgressDialog();
    try {
      const response = await this.upsertUseCase.execute(collection, payload);

      const auditMessage = this.generateAuditMessage(
        editedItems,
        existingEditedRawMaterials,
        isUpdate,
      );

      await this.createAuditLog({
        user,
        auditMessage,
        collection,
        responseId: response.id,
        documentId,
        existingObject,
        response,
        payload,
        storeId,
        isUpdate,
      });

      await this.createInventoryLogs({
        editedItems,
        existingEditedRawMaterials,
        isUpdate,
        user,
        storeId,
      });

      this.view.hideProgressDialog();
      this.view.navigateBack();
    } catch (error) {
      this.view.hideProgressDialog();
      console.error("Error saving actual count:", error);
      this.view.showError("Failed to save actual count. Please try again.");
    }
  }

  onSaveSuccess() {
    this.view.navigateBack();
    dialog.fire({
      html: (
        <DialogModal
          icon="bi bi-question-octagon-fill"
          iconColor="#82c14a"
          title="Saved Successfully"
          message=""
          isCancelButton={false}
          onSubmit={() => dialog.close()}
          onClose={() => dialog.close()}
        />
      ),
      footer: false,
    });
  }

  onSaveError(error) {
    console.error("Save error:", error);
  }

  initProgress(total) {
    this.view.showSaveProgress(total, `Saving ${total} items...`);
  }

  async generateFCVariant(ctx, editedFinals) {
    const allInventory = await findObjectUseCase().execute("inventory", {
      where: { "storeId.id": { $eq: ctx.storeId } },
      limit: 10000,
    });

    // const jobs = allInventory.map((inv) => {
    //   console.log("inv", inv);
    //   const invId = inv?.id || inv?.itemName;
    //   const final = editedFinals.get(invId);

    //   const systemCountNum = final
    //     ? Number(final.systemCount)
    //     : Number(inv?.systemCount || 0);
    //   const pCount =
    //     final && typeof final.actualCount === "number"
    //       ? String(final.actualCount)
    //       : undefined;

    //   const idealNum = Number(pCount || 0) - systemCountNum;
    //   const saleCountNum = Number(inv?.deductionCount || 0);
    //   const unitCost = Number(inv?.cost || 0);

    //   console.log("unitCost", unitCost);

    //   // only include ideal if negative
    //   const adjustedCount =
    //     idealNum < 0 ? saleCountNum + idealNum : saleCountNum;
    //   // const costofGoodsNum = Number(inv?.cost || 0) * saleCountNum;
    //   const costofGoodsNum = adjustedCount * unitCost;
    //   const variance = idealNum * unitCost;

    //   const key = `${ctx.storeId}|${invId}|${ctx.dayISO}`;

    //   return this.upsertUseCase.execute("fc_variant", {
    //     variantId: key,
    //     storeId: { id: ctx.storeId },
    //     item: {
    //       id: invId,
    //       name: final?.itemName || inv?.itemName,
    //       uom: final?.uomName || inv?.uom?.name || "",
    //     },
    //     variantDate: ctx.dayISO,
    //     fcProduct: final?.itemName || inv?.itemName,
    //     beginningInventory: String(systemCountNum),
    //     salesCount: String(saleCountNum || 0),
    //     ending: String(systemCountNum),
    //     pCount,
    //     costOfGoods: Number(costofGoodsNum).toFixed(2),
    //     ideal: String(idealNum),
    //     variant: String(variance),
    //     lastActualCountAt: pCount ? ctx.nowISO : undefined,
    //   });
    // });

    // const jobs = allInventory.map((inv) => {
    //   console.log("inv", inv);
    //   const invId = inv?.id || inv?.itemName;
    //   const final = editedFinals.get(invId);

    //   const systemCountNum = final
    //     ? Number(final.systemCount)
    //     : Number(inv?.systemCount || 0);
    //   const pCount =
    //     final && typeof final.actualCount === "number"
    //       ? String(final.actualCount)
    //       : undefined;

    //   // const idealNum = Number(pCount || 0) - systemCountNum;
    //   const idealNum = Number(pCount || 0) - Number(inv?.systemCountBefore);
    //   const saleCountNum = Number(inv?.deductionCount || 0);
    //   const unitCost = Number(inv?.cost || 0);

    //   console.log("unitCost", unitCost);

    //   // only include ideal if negative
    //   const adjustedCount =
    //     idealNum < 0 ? saleCountNum + idealNum : saleCountNum;
    //   // const costofGoodsNum = Number(inv?.cost || 0) * saleCountNum;
    //   const costofGoodsNum = adjustedCount * unitCost;
    //   const variance = idealNum * unitCost;

    //   const key = `${ctx.storeId}|${invId}|${ctx.dayISO}`;

    //   return this.upsertUseCase.execute("fc_variant", {
    //     variantId: key,
    //     storeId: { id: ctx.storeId },
    //     item: {
    //       id: invId,
    //       name: final?.itemName || inv?.itemName,
    //       uom: final?.uomName || inv?.uom?.name || "",
    //     },
    //     variantDate: ctx.dayISO,
    //     fcProduct: final?.itemName || inv?.itemName,
    //     beginningInventory: String(systemCountNum),
    //     salesCount: String(saleCountNum || 0),
    //     // ending: String(systemCountNum),
    //     ending: String(inv?.systemCountBefore),
    //     pCount,
    //     costOfGoods: Number(costofGoodsNum).toFixed(2),
    //     ideal: String(idealNum),
    //     variant: String(variance),
    //     lastActualCountAt: pCount ? ctx.nowISO : undefined,
    //   });
    // });

    const jobs = allInventory.map((inv) => {
      console.log("inv", inv);
      const invId = inv?.id || inv?.itemName;
      const final = editedFinals.get(invId);

      const systemCountNum = final
        ? Number(final.systemCount)
        : Number(inv?.systemCount || 0);
      const pCount =
        final && typeof final.actualCount === "number"
          ? String(final.actualCount)
          : undefined;

      const idealNum =
        Number(pCount || 0) - Number(inv?.systemCountBefore || 0);
      // const idealNum = Number(pCount || 0) - Number(inv?.systemCountBefore);
      const saleCountNum = Number(inv?.deductionCountPrevious || 0);
      const unitCost = Number(inv?.cost || 0);

      console.log("unitCost", unitCost);

      // only include ideal if negative
      const adjustedCount =
        idealNum < 0 ? saleCountNum + idealNum : saleCountNum;
      // const costofGoodsNum = Number(inv?.cost || 0) * saleCountNum;
      const costofGoodsNum = adjustedCount * unitCost;
      const variance = idealNum * unitCost;
      const beginningInv =
        Number(inv?.systemCountBefore) + Number(inv?.deductionCountPrevious);

      const key = `${ctx.storeId}|${invId}|${ctx.dayISO}`;

      return this.upsertUseCase.execute("fc_variant", {
        variantId: key,
        storeId: { id: ctx.storeId },
        item: {
          id: invId,
          name: final?.itemName || inv?.itemName,
          uom: final?.uomName || inv?.uom?.name || "",
        },
        variantDate: ctx.dayISO,
        fcProduct: final?.itemName || inv?.itemName,
        // beginningInventory: String(systemCountNum),
        beginningInventory: String(beginningInv || 0),
        salesCount: String(saleCountNum || 0),
        // ending: String(systemCountNum),
        ending: String(inv?.systemCountBefore || 0),
        pCount,
        costOfGoods: Number(costofGoodsNum || 0).toFixed(2),
        ideal: String(idealNum || 0),
        variant: String(variance || 0),
        lastActualCountAt: pCount ? ctx.nowISO : undefined,
      });
    });

    await Promise.all(jobs);
  }

  async finalizeIfActualCount(ctx, { editedFinals }) {
    if (ctx.adjustmentType !== "Actual Count") return;
    await this.generateFCVariant(ctx, editedFinals);
  }

  async saveCountLines(lines) {
    return Promise.all(
      lines.map((l) => this.upsertUseCase.execute("inventory_count_lines", l)),
    );
  }

  buildCountLine(ctx, countHeader, row, existing, saved, systemQty) {
    const countId = countHeader.id;
    return {
      countId: countId,
      storeId: { id: ctx.storeId },
      businessDate: ctx.businessDate,
      sku: row.sku,
      inventoryItem: saved.itemName,
      systemQty: String(systemQty),
      actualQty: String(row.quantity),
      variance: String(row.quantity - systemQty),
      cost: String(row.cost),
    };
  }

  async updateActualCountLogs(ctx, mergedEditedRaw) {
    const payload = {
      id: ctx?.object?.id,
      edited_raw: mergedEditedRaw,
      status: "Done",
    };
    return this.upsertUseCase.execute("actual_count_logs", payload);
  }

  async writeAuditLog(ctx, before, after, oldQty, newQty) {
    const payload = {
      user: { id: ctx.user.id },
      logDate: ctx.nowISO,
      message: `${ctx.adjustmentType}: ${after.itemName} from ${String(oldQty)} to ${String(newQty)}`,
      collection: ctx.collection,
      objectId: after.id,
      payload: { before: before || null, after },
      store: { id: ctx.storeId },
    };

    return auditLogUseCase().create(payload);
  }

  async writeInventoryLog(ctx, saved, oldQty, newQty, countHeader) {
    const payload = {
      user: { id: ctx.user.id },
      itemName: saved.itemName,
      adjustmentType: ctx.adjustmentType,
      oldQuantity: String(oldQty),
      newQuantity: String(newQty),
      storeId: { id: ctx.storeId },
      reference: countHeader ? { countId: countHeader.countId } : undefined,
    };

    return this.upsertUseCase.execute("inventory_logs", payload);
  }

  async upsertInventoryItem(ctx, row, existing, computed, currentCount) {
    // const payload = {
    //   id: existing?.id,
    //   sku: row.sku,
    //   itemName: row.itemName || existing?.itemName,
    //   cost: String(row.cost),
    //   systemCount: String(computed.systemCount),
    //   actualCount:
    //     ctx.adjustmentType === "Actual Count"
    //       ? String(row.quantity)
    //       : undefined,
    //   uom: row.uom || existing?.uom || {},
    //   lastAdjusted: ctx.nowISO,
    //   adjustedBy: { id: ctx.user.id },
    //   acl: ctx.acl,
    //   storeId: { id: ctx.storeId },
    //   ...(row.category && { category: row.category }),
    // };
    const payload = {
      id: existing?.id,
      sku: row.sku,
      itemName: row.itemName || existing?.itemName,
      cost: String(row.cost),
      deductionCount: String(0),
      deductionCountPrevious: String(existing?.deductionCount),
      systemCount: String(computed.systemCount),
      systemCountBefore: String(currentCount),
      beginningCount:
        ctx.adjustmentType === "Actual Count"
          ? String(row.quantity)
          : undefined,
      actualCount:
        ctx.adjustmentType === "Actual Count"
          ? String(row.quantity)
          : undefined,
      uom: row.uom || existing?.uom || {},
      lastAdjusted: ctx.nowISO,
      adjustedBy: { id: ctx.user.id },
      acl: ctx.acl,
      storeId: { id: ctx.storeId },
      ...(row.category && { category: row.category }),
    };

    return this.upsertUseCase.execute("inventory", payload);
  }

  computeCounts({ adjustmentType, quantity, currentCount, afterFive, is24_7 }) {
    let systemCount = currentCount;
    let actualCount = null;

    if ("Actual Count" === "Actual Count") {
      actualCount = quantity;

      if (afterFive && is24_7) systemCount = actualCount;
      else systemCount = 0;

      return { systemCount, actualCount };
    }

    return { systemCount, actualCount };
  }

  async processRows({ ctx, rows, invBySku, countHeader }) {
    const editedFinals = new Map();
    const countLines = [];
    let done = 0;

    for (const row of rows) {
      const existing = invBySku.get(row.sku);
      const currentCount = toNumber(existing?.systemCount);
      const mergedEditedRaw = this.mergedEditedRaw;
      const computed = this.computeCounts({
        adjustmentType: ctx.adjustmentType,
        quantity: row.quantity,
        currentCount,
        afterFive: ctx.afterFive,
        is24_7: ctx.is24_7,
      });

      const saved = await this.upsertInventoryItem(
        ctx,
        row,
        existing,
        computed,
        currentCount,
      );

      await this.writeInventoryLog(
        ctx,
        saved,
        currentCount,
        computed.systemCount,
        countHeader,
      );

      await this.writeAuditLog(
        ctx,
        existing,
        saved,
        currentCount,
        computed.systemCount,
      );

      await this.updateActualCountLogs(ctx, mergedEditedRaw);

      if (ctx.adjustmentType === "Actual Count") {
        countLines.push(
          this.buildCountLine(
            ctx,
            countHeader,
            row,
            existing,
            saved,
            currentCount,
          ),
        );
      }

      editedFinals.set(saved.id || existing?.id || row.itemName, {
        systemCount: computed.systemCount,
        actualCount:
          ctx.adjustmentType === "Actual Count" ? row.quantity : undefined,
        itemName: saved.itemName || row.itemName,
        uomName: row?.uom?.name || existing?.uom?.name || "",
      });

      // progress bar
      done++;
      this.view.updateSaveProgress(
        done,
        `Saving: ${saved.itemName || row.itemName || "Item"}`,
      );
      if (done % 20 === 0) await new Promise((r) => setTimeout(r, 0));
    }

    if (countLines.length) await this.saveCountLines(countLines);
    return { editedFinals };
  }

  async saveStoreInventoryStatus(ctx) {
    return this.upsertUseCase.execute("storeInventoryStatus", {
      store: { id: ctx.storeId },
      storeCode: ctx.storeCode,
      businessDate: ctx.businessDate,
      isActualCountDone: true,
    });
  }

  async ensureCountHeaderIfNeeded(ctx) {
    if (ctx.adjustmentType !== "Actual Count") return null;

    const countId = `${ctx.storeCode}-${ctx.businessDate}`;
    const header = await this.upsertUseCase.execute("inventory_counts", {
      id: countId,
      storeId: { id: ctx.storeId },
      countedBy: { id: ctx.user.id },
      status: "Submitted",
      storeCode: ctx.storeCode,
      businessDate: ctx.businessDate,
    });

    await this.saveStoreInventoryStatus(ctx);
    return { ...header, countId };
  }

  async preloadInventoryBySku(storeId) {
    const list = await findObjectUseCase().execute("inventory", {
      where: { "storeId.id": { $eq: storeId } },
      limit: 10000,
    });

    const map = new Map();
    list.forEach((inv) => map.set(inv.sku, inv));
    return map;
  }

  mergeEditedItemsWithEditedRaw(editedRaw, editedItems) {
    const editedItemsMap = new Map();
    Object.values(editedItems).forEach((item) => {
      editedItemsMap.set(item.sku, item);
    });

    // Create a map from edited_raw for tracking
    const editedRawMap = new Map();
    editedRaw.forEach((item) => {
      editedRawMap.set(item.sku, item);
    });

    // Update existing items in edited_raw
    const updatedItems = editedRaw.map((item) => {
      const newEdit = editedItemsMap.get(item.sku);
      if (newEdit) {
        return {
          ...item,
          quantity: newEdit.quantity,
        };
      }
      return item;
    });

    //ADD new items from editedItems that aren't in edited_raw
    editedItemsMap.forEach((editItem, sku) => {
      if (!editedRawMap.has(sku)) {
        updatedItems.push({
          sku: editItem.sku,
          quantity: editItem.quantity,
        });
      }
    });

    return updatedItems;
  }

  loadEditedRows(ctx) {
    const editedRaw = ctx.object.edited_raw || [];
    const mergedEditedRaw = this.mergeEditedItemsWithEditedRaw(
      editedRaw,
      ctx.editedItems || {},
    );
    this.mergedEditedRaw = mergedEditedRaw;

    // Map to the format needed for the table
    return mergedEditedRaw.map((editedItem) => {
      const found = ctx.infiniteItems.find((x) => x.sku === editedItem.sku);

      return {
        id: found?.id || editedItem.sku,
        sku: editedItem.sku,
        itemName: found?.itemName || "",
        cost: toNumber(found?.cost ?? 0),
        quantity: toNumber(editedItem.quantity),
        category: found?.category,
        uom: found?.uom,
        found,
        edited: editedItem,
      };
    });
  }

  validateContext(ctx) {
    if (!ctx.user?.id) throw new Error("Missing user");
    if (!ctx.storeId) throw new Error("Missing storeId");
  }

  async resolveActualCountBusinessDate({ storeCode, now }) {
    const today = toISODate(now);
    const yesterday = addDaysISO(today, -1);

    const [last] = await findObjectUseCase().execute("storeInventoryStatus", {
      where: { storeCode: storeCode, isActualCountDone: true },
      sort: { businessDate: -1 },
      limit: 1,
    });

    const lastClosed = last?.businessDate ? toISODate(last.businessDate) : null;
    const nextToClose = lastClosed ? addDaysISO(lastClosed, 1) : yesterday;
    if (nextToClose < today) return nextToClose;
    return today;
  }

  async getContext() {
    const now = new Date();
    const nowISO = now.toISOString();
    const user = this.view.getCurrentUser();
    const storeAnalytic = user?.storeAnalytic?.[0];
    const operationType =
      this.userLoggedIn[0]?.storeAnalytic?.[0]?.operationType;
    const is24_7 = operationType === "24/7 Store";
    const afterFive = isAfterFiveAMManila(now);
    const storeId = storeAnalytic?.id;
    const storeCode = this.userLoggedIn[0]?.storeAnalytic?.[0]?.name;
    const adjustmentType = "Actual Count" || this.view.getAdjustmentType();

    let businessDate;

    if (adjustmentType === "Actual Count") {
      businessDate = await this.resolveActualCountBusinessDate({
        storeCode,
        now,
      });
    } else {
      businessDate = getBusinessDate(now);
    }

    return {
      user,
      storeAnalytic,
      storeId,
      storeCode,
      now,
      nowISO,
      dayISO: nowISO.slice(0, 10),
      businessDate,
      collection: this.view.getCollectionName(),
      adjustmentType: adjustmentType,
      acl: this.view.getAcl(),
      afterFive,
      is24_7,
      editedItems: this.view.state.selectedQuantities || {},
      infiniteItems: this.view.state.infiniteItems || [],
      object: this.view.state.object || {},
    };
  }

  async save() {
    this.view.showProgressDialog();

    try {
      const ctx = await this.getContext();
      this.validateContext(ctx);
      const rows = this.loadEditedRows(ctx);
      this.initProgress(rows.length);
      const invBySku = await this.preloadInventoryBySku(ctx.storeId);
      const countHeader = await this.ensureCountHeaderIfNeeded(ctx);
      const results = await this.processRows({
        ctx,
        rows,
        invBySku,
        countHeader,
      });
      await this.finalizeIfActualCount(ctx, results);
      this.view.hideProgressDialog();
      this.onSaveSuccess();
    } catch (error) {
      this.onSaveError(error);
      console.log("err", error);
    } finally {
      this.view.hideProgressDialog();
    }
  }

  async submit(adjustmentType) {
    try {
      this.view.submitting();
      dialog.close();
      if (adjustmentType) {
        await this.saveActual();
      } else {
        await this.save();
      }

      dialog.fire({
        html: (
          <DialogModal
            icon="bi bi-question-octagon-fill"
            iconColor="#82c14a"
            title="Saved Successfully"
            message=""
            isCancelButton={false}
            onSubmit={() => {
              dialog.close();
            }}
            onClose={() => dialog.close()}
          />
        ),
        footer: false,
      });
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }

  searchSubmit(search) {
    this.reset();
    this.search = search;
    this.getObjects();
  }
}

export default PartialLogsFormPresenter;
